import { Capacitor } from '@capacitor/core';

/**
 * Returns true when running on an iOS device (iPhone/iPod/iPad).
 *
 * iPadOS 13+ reports a desktop "Macintosh" user agent, so we additionally
 * sniff for a touch-capable Mac to catch iPads in desktop mode.
 *
 * Used to gate WebKit-specific audio workarounds (e.g. the audio-session
 * "ducking" that lingers after a WebRTC voice chat). See `voice_disconnected`
 * handling in useAudioPlayer and the mic teardown in useRTVIClient.
 */
export const isIOS = (): boolean => {
  if (typeof navigator === 'undefined' || typeof document === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS masquerading as macOS
  return ua.includes('Macintosh') && 'ontouchend' in document;
};

/**
 * Returns true when the bundle is running inside the native Capacitor shell
 * (the iOS app), false in any browser — including mobile Safari and the
 * installed PWA.
 *
 * This is the one greppable switch for native-only behaviour. Audit every call
 * site before an App Store submission; in particular the native bundle must
 * never render purchase UI, prices, or copy steering users to the website to
 * pay (App Store guideline 3.1.3(a), the reader-app model — the app honours
 * tiers bought on the web but sells nothing).
 *
 * Safe to call at module scope and in a plain browser: with no native bridge
 * present Capacitor resolves its platform to 'web' and this returns false.
 */
export const isNativeApp = (): boolean => Capacitor.isNativePlatform();

/**
 * Custom DOM event dispatched on `document` when the voice chat disconnects.
 * Listeners use it to undo iOS audio-session ducking that otherwise leaves
 * audiobook playback quiet after a chat. Dispatched from useRTVIClient.
 */
export const VOICE_DISCONNECTED_EVENT = 'voice_disconnected';
